import { Card, CardContent, Skeleton, Text } from '@billda/design-system';
import {
  ComposedChart,
  Area,
  Line,
  ReferenceLine,
  ReferenceDot,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useSiteBurndown } from '../hooks/useSiteBurndown';
import type { BurndownDataPoint } from '../api/sitesApi';
import { formatCurrencyWhole } from '../../../shared/utils/currency';

// Booked labour, stacked as filled areas that build up from zero over time.
const AREA_SERIES = {
  paid: { color: '#4CAF50', label: 'Paid' },
  inProgress: { color: '#2196F3', label: 'In Progress' },
} as const;

// Additional cost on top of the labour scope — a band offset above the
// Total labour line, from totalLabourValue up to totalLabourValue + variations.
const VARIATIONS = { color: '#FFC107', label: 'Variations' } as const;

// Remaining labour value, overlaid as a line that burns down.
const UNBOOKED = { color: '#9E9E9E', label: 'Unbooked' } as const;

// Total labour value — constant reference ceiling.
const TOTAL_COLOR = 'rgba(255,255,255,0.55)';

const GRADIENT_SERIES = [
  { key: 'paid', color: AREA_SERIES.paid.color },
  { key: 'inProgress', color: AREA_SERIES.inProgress.color },
  { key: 'variations', color: VARIATIONS.color },
];

interface BurndownChartDatum extends BurndownDataPoint {
  // Total project value at this point: totalLabourValue + cumulative variations.
  grandTotal: number;
}

function formatYAxis(value: number): string {
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(0)}k`;
  }
  return `£${value}`;
}

function formatXAxisDate(
  date: string,
  _index: number,
  totalPoints: number
): string {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  if (totalPoints > 52) {
    return month;
  }
  return `${day} ${month}`;
}

interface SiteBurndownChartProps {
  siteId: number;
}

export function SiteBurndownChart({ siteId }: SiteBurndownChartProps) {
  const { data, isLoading } = useSiteBurndown(siteId);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const dataPoints = data?.dataPoints ?? [];
  const totalLabourValue = data?.totalLabourValue ?? 0;

  const chartData: BurndownChartDatum[] = dataPoints.map(point => ({
    ...point,
    grandTotal: totalLabourValue + point.variations,
  }));

  const hasVariations = dataPoints.some(point => point.variations > 0);
  const lastDatum = chartData[chartData.length - 1];

  return (
    <Card data-testid="site-burndown-chart">
      <CardContent className="p-6">
        <div className="mb-6">
          <Text variant="body1" className="font-bold">
            Site Burndown
          </Text>
          <Text variant="body2" color="secondary" className="mt-0.5">
            {dataPoints.length === 0
              ? 'No data yet'
              : `${dataPoints.length} ${dataPoints.length === 1 ? 'day' : 'days'}`}
          </Text>
        </div>

        <div
          className="h-62.5 w-full sm:h-75"
          data-testid="site-burndown-chart-area"
        >
          {dataPoints.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 12, right: 48, left: 5, bottom: 5 }}
              >
                <defs>
                  {GRADIENT_SERIES.map(({ key, color }) => (
                    <linearGradient
                      key={key}
                      id={`burndown-gradient-${key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop
                        offset="100%"
                        stopColor={color}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value, index) =>
                    formatXAxisDate(value, index, dataPoints.length)
                  }
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                  domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  formatter={(value, name, item) => {
                    if (name === VARIATIONS.label) {
                      const datum = item?.payload as
                        | BurndownChartDatum
                        | undefined;
                      const variation = datum?.variations ?? 0;
                      return `${formatCurrencyWhole(variation)} (${formatCurrencyWhole(Number(value))})`;
                    }
                    return formatCurrencyWhole(Number(value));
                  }}
                  labelStyle={{
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 4,
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                />
                {(
                  Object.entries(AREA_SERIES) as [
                    keyof typeof AREA_SERIES,
                    (typeof AREA_SERIES)[keyof typeof AREA_SERIES],
                  ][]
                ).map(([key, { color, label }]) => (
                  <Area
                    key={key}
                    type="monotoneX"
                    dataKey={key}
                    name={label}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#burndown-gradient-${key})`}
                    stackId="1"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                ))}
                {hasVariations && (
                  <Area
                    type="monotoneX"
                    dataKey="grandTotal"
                    name={VARIATIONS.label}
                    baseValue={totalLabourValue}
                    stroke={VARIATIONS.color}
                    strokeWidth={2}
                    fill="url(#burndown-gradient-variations)"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                )}
                <Line
                  type="monotoneX"
                  dataKey="unbooked"
                  name={UNBOOKED.label}
                  stroke={UNBOOKED.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
                {totalLabourValue > 0 && (
                  <ReferenceLine
                    y={totalLabourValue}
                    stroke={TOTAL_COLOR}
                    strokeDasharray="6 4"
                    strokeWidth={1.5}
                    ifOverflow="extendDomain"
                    label={{
                      value: 'Total labour',
                      position: 'insideTopRight',
                      fill: TOTAL_COLOR,
                      fontSize: 11,
                    }}
                  />
                )}
                {hasVariations && lastDatum && (
                  <ReferenceDot
                    x={lastDatum.date}
                    y={lastDatum.grandTotal}
                    r={0}
                    ifOverflow="extendDomain"
                    label={{
                      value: formatCurrencyWhole(lastDatum.variations),
                      position: 'top',
                      fill: VARIATIONS.color,
                      fontSize: 11,
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <Text variant="body2" color="secondary">
                No allocation data available
              </Text>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Card data-testid="site-burndown-chart-loading">
      <CardContent className="p-6">
        <Skeleton width={200} height={24} className="mb-1" />
        <Skeleton width={120} height={18} className="mb-6" />
        <Skeleton variant="rectangular" height={300} className="rounded-lg" />
      </CardContent>
    </Card>
  );
}
